import React, { useEffect, useState } from 'react';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const H2 = ({ children }) => (
  <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50 mt-6 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 mt-4 mb-1">{children}</h3>
);
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-1 mt-1">{children}</ul>;

const Row = ({ a, b, c }) => (
  <tr className="align-top border-t border-slate-200 dark:border-slate-700">
    <td className="py-1.5 pr-3 font-medium">{a}</td>
    <td className="py-1.5 pr-3">{b}</td>
    {c !== undefined && <td className="py-1.5">{c}</td>}
  </tr>
);

// Texte de la charte d'utilisation et d'information RGPD (art. 13). Le même
// composant sert à la page publique /charte et à l'écran de signature : le
// texte exact affiché (innerText) est celui qui est archivé et signé.
// Toute modification du texte doit s'accompagner d'un changement de
// CHARTE_VERSION (backend/rgpd_utils.py) pour imposer une nouvelle signature.
export const CharteTexte = ({ info }) => {
  if (!info) return null;
  const contact = info.contact_rgpd;
  return (
    <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
      <p className="text-xs text-slate-500">Version {info.version} · {info.app}</p>

      <H2>Partie 1 — À quoi sert l'application et règles d'utilisation</H2>

      <H3>1. Objet et champ d'application</H3>
      <p>
        L'application « {info.app} » est l'outil professionnel de {info.organisme} pour tenir la main courante
        des sites surveillés, diffuser les consignes, rédiger les comptes rendus et le reporting, échanger en
        messagerie interne et partager le calendrier et l'organigramme. Elle s'applique à toute personne
        disposant d'un compte (agent, chef de poste, administrateur, compte de site partagé ou client). Son usage
        est strictement professionnel : toute utilisation personnelle ou étrangère à l'activité est interdite.
      </p>

      <H3>2. Compte personnel et confidentialité des accès</H3>
      <UL>
        <li>Chaque compte est personnel : ne communiquez jamais vos identifiants (mot de passe, code personnel à 4 chiffres), même à un collègue.</li>
        <li>N'utilisez jamais le compte d'une autre personne, même avec son accord.</li>
        <li>Déconnectez-vous en quittant votre poste, surtout sur un appareil partagé, et signalez sans délai tout usage suspect de votre compte.</li>
        <li>Sur un compte de site partagé, l'agent rédacteur doit toujours être renseigné et confirmé par son code personnel, pour garantir la traçabilité individuelle.</li>
        <li>Vous êtes responsable de toute action réalisée depuis votre compte.</li>
      </UL>

      <H3>3. Main courante électronique</H3>
      <p>
        La main courante est un document professionnel à valeur probante. Chaque événement doit être renseigné de
        façon exacte, sincère et sans délai. Une entrée enregistrée ne peut être ni modifiée ni supprimée : une
        erreur se corrige uniquement par la fonction de correction tracée, qui conserve l'historique. Il est
        interdit de consigner des informations mensongères, diffamatoires ou de nature à induire en erreur. Le
        texte libre ne doit contenir que les faits utiles : relatez ce que vous avez constaté, sans jugement et
        sans données inutiles sur les personnes (voir la partie 2, article D).
      </p>

      <H3>4. Confidentialité des consignes et des événements</H3>
      <p>
        Les consignes, les événements de la main courante, les comptes rendus, les rapports et toutes les
        informations de l'application sont confidentiels. Vous ne devez en parler ou les transmettre qu'aux
        personnes habilitées à les connaître dans le cadre de la mission. Les consignes doivent être lues et
        appliquées sans délai ; lorsqu'une signature est demandée, elle vaut prise de connaissance et engagement
        à les respecter.
      </p>

      <H3>5. Photos, pièces jointes et copies hors de l'outil</H3>
      <UL>
        <li>Les photographies et fichiers joints doivent avoir un lien direct avec la mission et être ajoutés depuis l'application.</li>
        <li>Il est interdit de photographier l'écran, de faire des captures d'écran ou de copier, transférer ou publier des données de l'application (messagerie personnelle, réseaux sociaux, cloud personnel, etc.).</li>
        <li>Une photo prise avec un téléphone personnel pour la main courante doit être supprimée de l'appareil (galerie, sauvegarde en ligne) une fois ajoutée à l'application.</li>
        <li>Ne photographiez pas de personnes sans nécessité : n'ajoutez des visages, plaques d'immatriculation ou documents que si c'est indispensable à l'événement.</li>
      </UL>

      <H3>6. Messagerie interne, comptes rendus, calendrier et organigramme</H3>
      <p>
        La messagerie est un outil de communication professionnelle : courtoisie, aucun propos injurieux ou
        discriminatoire, aucune information confidentielle à une personne non habilitée, échanges avec les
        comptes clients réservés aux personnes autorisées. Les comptes rendus et le reporting doivent refléter
        fidèlement les faits ; ils peuvent être remis aux clients ou aux autorités et leur exactitude engage leur
        auteur. Le calendrier et l'organigramme (identité, fonction, photographie) sont réservés à un usage
        interne.
      </p>

      <H3>7. Sécurité et manquements</H3>
      <p>
        Il est interdit de contourner les protections de l'application, d'accéder à des données ou fonctions non
        autorisées pour votre rôle ou de perturber le système ; toute faille constatée doit être signalée à
        l'administrateur. Tout manquement à cette charte peut entraîner un rappel à l'ordre, une sanction
        disciplinaire, la suspension ou la suppression du compte, sans préjudice des suites judiciaires,
        notamment en cas de fausse déclaration dans un document à valeur probante ou d'atteinte aux données
        personnelles d'un tiers.
      </p>

      <H2>Partie 2 — Vos données personnelles (information RGPD, article 13)</H2>

      <H3>A. Responsable du traitement</H3>
      <p>
        {info.organisme} — SIRET {info.siret} — {info.adresse}.<br />
        Contact pour toute question ou demande relative à vos données :{' '}
        <a className="underline text-indigo-700 dark:text-indigo-300" href={`mailto:${contact}`}>{contact}</a>.
      </p>

      <H3>B. Pourquoi vos données sont traitées (finalités et bases légales)</H3>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm mt-1">
          <thead>
            <tr className="text-xs uppercase text-slate-500">
              <th className="pr-3 pb-1">Finalité</th><th className="pb-1">Base légale</th>
            </tr>
          </thead>
          <tbody>
            <Row a="Gestion des comptes, authentification et droits d'accès" b="Exécution du contrat de travail ou du contrat de prestation ; intérêt légitime de sécuriser l'accès" />
            <Row a="Tenue de la main courante, des consignes, des comptes rendus et du reporting : traçabilité et preuve des missions, information des clients, gestion des litiges" b="Intérêt légitime de l'entreprise ; exécution des contrats conclus avec nos clients ; obligations professionnelles de la sécurité privée" />
            <Row a="Assistance aux personnes (secours) et fiches d'intervention" b="Intérêt légitime ; pour les données de santé : intérêt vital de la personne secourue. Les informations de secours sont transmises au client, qui traite ensuite les données de la personne pour son propre compte" />
            <Row a="Signalement d'infractions constatées (appréhensions, vols) et suites données" b="Intérêt légitime de l'entreprise et de ses clients ; données traitées sous le strict contrôle du responsable (art. 10)" />
            <Row a="Messagerie, calendrier, annuaire et organigramme" b="Exécution du contrat de travail ; intérêt légitime d'organisation du service" />
            <Row a="Sécurité de l'application (journal de connexion, journal d'activité, contrôle d'accès)" b="Intérêt légitime ; obligation de sécuriser les données (art. 32)" />
            <Row a="Preuve de la signature de cette charte" b="Intérêt légitime ; obligation de démontrer la conformité (art. 5.2)" />
          </tbody>
        </table>
      </div>

      <H3>C. Données collectées</H3>
      <UL>
        <li><strong>Votre compte :</strong> nom, email, téléphone, rôle et sites d'affectation, mot de passe et code personnel (conservés sous forme chiffrée, illisible), photo, date de naissance, numéro de carte professionnelle et documents que vous ajoutez à votre profil, intitulé de poste.</li>
        <li><strong>Votre activité :</strong> événements, comptes rendus, consignes signées, messages, rendez-vous, reportings, signatures manuscrites (charte, événements, consignes), fiches de contrôle vous concernant, avec date et heure.</li>
        <li><strong>Données techniques :</strong> journal de connexion (date, adresse IP, navigateur), journal d'activité, preuve de signature de la charte (adresse IP, navigateur, empreintes).</li>
        <li><strong>Données concernant des tiers</strong> saisies dans la main courante : voir article D.</li>
      </UL>

      <H3>D. Données de tiers : visiteurs, personnes appréhendées, victimes, plaques d'immatriculation</H3>
      <p>
        La main courante peut mentionner des personnes qui n'ont pas de compte (visiteurs, clients, personnes
        appréhendées, victimes, témoins, véhicules). Pour protéger leurs données :
      </p>
      <UL>
        <li><strong>n'indiquez jamais l'identité d'une personne</strong> (nom, prénom, adresse, téléphone, date de naissance) dans la main courante, qu'il s'agisse d'une personne appréhendée, d'une victime ou d'un témoin : décrivez-la de façon neutre (« un homme », « une cliente ») ; une plaque d'immatriculation n'est notée que si elle est indispensable ;</li>
        <li>ne notez que ce qui est strictement nécessaire à la mission : faits constatés, heure, lieu, rôle de la personne ;</li>
        <li>ne notez jamais d'origine supposée, d'opinion politique ou religieuse, d'appartenance syndicale, de vie sexuelle ou d'autre information sans rapport avec les faits ;</li>
        <li><strong>santé :</strong> en cas de secours, indiquez seulement la nature (malaise, blessure), les gestes réalisés et le service appelé, sans diagnostic ni antécédents ; la fiche de secours est transmise au client, qui traite ensuite les données de la personne ;</li>
        <li><strong>infractions :</strong> décrivez les faits constatés, sans qualification juridique ni jugement de culpabilité ; ces données ne servent qu'à la sécurité du site, à l'information du client et aux suites légales ;</li>
        <li>n'enregistrez pas de documents d'identité ni de photos de personnes sans nécessité.</li>
      </UL>
      <p className="mt-2">
        <strong>Droits des tiers :</strong> toute personne citée dans la main courante peut demander l'accès aux
        données qui la concernent, leur rectification ou, dans les limites de la loi, leur effacement ou une
        limitation, en écrivant à {contact}. Comme la main courante ne contient pas l'identité des personnes, nous ne pouvons en principe pas les y retrouver : précisez la date, l'heure et le lieu des faits. Nous répondons dans un délai d'un mois. L'accès peut être limité pour
        protéger les droits d'autres personnes (par exemple l'identité d'un agent ou d'un témoin) ou lorsque la
        conservation est nécessaire à la constatation, l'exercice ou la défense d'un droit en justice.
      </p>

      <H3>E. Qui peut voir quoi</H3>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm mt-1">
          <thead><tr className="text-xs uppercase text-slate-500"><th className="pr-3 pb-1">Qui</th><th className="pb-1">Accès</th></tr></thead>
          <tbody>
            <Row a="Vous (agent)" b="Votre compte et vos données ; les événements, consignes et messages des sites auxquels vous êtes affecté." />
            <Row a="Chef de poste" b="Ce que voit un agent, plus : les comptes rendus, le reporting, les profils et fiches de contrôle des agents de ses sites, le journal d'activité de ses sites." />
            <Row a="Client" b="Les événements, rapports et documents de ses sites qui lui sont ouverts ; pas les données personnelles internes des agents (profil, documents). Ses consultations sont tracées." />
            <Row a="Administrateur" b="L'ensemble des données de l'application, pour la gestion des comptes, la sécurité et les demandes d'exercice de droits." />
            <Row a="Hébergeur" b={`${info.hebergeur} : hébergement technique des serveurs et sauvegardes, sans accès aux contenus.`} />
            <Row a="Prestataires techniques" b="Brevo (envoi des emails : rapports PDF, notifications, réinitialisation de mot de passe) ; LanguageTool (correction orthographique : le texte que vous soumettez à la correction est envoyé à ce service, hébergé par un tiers sans garantie contractuelle propre à notre entreprise — n'y soumettez aucune donnée sensible) ; dictée vocale, fournie par votre navigateur (le son peut être traité par Google ou Apple selon l'appareil)." />
            <Row a="Autorités" b="Sur réquisition légale uniquement (police, justice)." />
          </tbody>
        </table>
      </div>

      <H3>F. Combien de temps vos données sont conservées</H3>
      <UL>
        <li><strong>Compte :</strong> tant qu'il est actif ; après désactivation, {info.conservation_compte_mois} mois, puis effacement de vos données personnelles (profil, photo, documents, coordonnées).</li>
        <li><strong>Main courante et documents à valeur probante</strong> (événements, comptes rendus, consignes, fiches de contrôle) : {info.conservation_evenements_ans} ans à partir des faits, durée de prescription des actions en justice. Après la suppression de votre compte, ces documents restent mais votre nom en est retiré (« Compte supprimé »).</li>
        <li><strong>Photos et pièces jointes des événements :</strong> {info.conservation_pieces_jointes_mois} mois. Les plaintes et feuilles d'appréhension sont archivées sur papier, dans un dossier séparé ; les fiches de secours sont transmises au client.</li>
        <li><strong>Fiches terrain (secours) :</strong> 30 jours, puis suppression automatique.</li>
        <li><strong>Journal d'activité :</strong> 12 mois. <strong>Journal de connexion :</strong> 6 mois. <strong>Messages privés :</strong> 5 ans.</li>
        <li><strong>Preuve de signature de la charte :</strong> conservée {info.conservation_preuves_ans} ans après l'effacement du compte, pour démontrer la conformité.</li>
        <li><strong>Sauvegardes :</strong> 14 jours.</li>
      </UL>

      <H3>G. Sécurité</H3>
      <p>
        Connexion chiffrée (HTTPS), mots de passe et codes personnels conservés sous forme chiffrée, droits
        d'accès par rôle et par site, jetons de connexion signés et limités dans le temps, limitation des tentatives de
        connexion, accès aux pièces jointes réservé aux personnes concernées, journalisation des connexions et des
        actions, sauvegardes régulières, hébergement en France.
      </p>

      <H3>H. Vos droits et recours</H3>
      <p>
        Vous avez le droit d'accéder à vos données et d'en obtenir une copie (bouton « Télécharger mes données »
        dans « Mon compte »), de les faire rectifier (« Mon compte »), de demander leur effacement, leur limitation,
        de vous opposer à un traitement fondé sur l'intérêt légitime et de définir des directives sur le sort de
        vos données après votre décès. Pour exercer un droit que l'application ne permet pas directement,
        écrivez à <a className="underline text-indigo-700 dark:text-indigo-300" href={`mailto:${contact}`}>{contact}</a>.
        Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez
        introduire une réclamation auprès de la CNIL (cnil.fr — 3 place de Fontenoy, TSA 80715, 75334 Paris
        Cedex 07). La fourniture des données du compte est nécessaire pour utiliser l'application ; sans elles,
        l'accès ne peut pas être ouvert.
      </p>

      <H2>Partie 3 — Acceptation</H2>
      <p>
        En signant ci-dessous, vous reconnaissez avoir lu l'intégralité de cette charte (règles d'utilisation et
        information sur vos données personnelles), en accepter les règles et vous engager à les respecter pendant
        toute la durée d'utilisation de votre compte. Votre signature, la date, l'heure, votre adresse IP et votre
        navigateur sont enregistrés comme preuve, avec l'empreinte du texte signé. Cette charte peut évoluer :
        une nouvelle signature vous sera alors demandée.
      </p>
    </div>
  );
};

export const useCharteInfo = () => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/charte`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => { if (!cancelled) setInfo(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);
  return { info, error };
};

// Page publique, accessible avant la connexion (/charte)
const Charte = () => {
  const { info, error } = useCharteInfo();
  const connected = !!localStorage.getItem('token');
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-6 px-4">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-5 sm:p-8" data-testid="charte-page">
        <a href={connected ? '/' : '/login'} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4" /> {connected ? "Retour à l'application" : 'Retour à la connexion'}
        </a>
        <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-slate-50">
          <ShieldCheck className="h-6 w-6 text-indigo-600" />
          Charte d'utilisation et protection des données
        </h1>
        {error && <p className="text-red-600 mt-4">Impossible de charger la charte, merci de réessayer.</p>}
        {!info && !error && <p className="mt-4 text-slate-500">Chargement…</p>}
        <CharteTexte info={info} />
      </div>
    </div>
  );
};

export default Charte;
